import {
	Button,
	Chip,
	ChipConfirmModal,
	ChipInput,
	ChipLink,
	ChipModal,
	ChipModalBody,
	ChipModalHeader,
	ChipSelect,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	EmptyState,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ondit/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, FolderOpen, MessageCircleQuestion, MoreHorizontal, Plus } from "lucide-react";
import { Fragment, type ReactNode, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { toast } from "sonner";
import { RunWithAgentModal, useAgentRun, useRunWithAgentLabel } from "~/common/agent-run/run-with-agent";
import { GapBrief } from "~/common/article/gap-brief";
import { useAccess, whyDisabled } from "~/core/access/index";
import {
	ApiError,
	api,
	type ContentArticle,
	type ContentCounts,
	type ContentFilter,
	type ContentGap,
	type GapState,
} from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { article as articlePath } from "~/core/paths";
import { keys, read } from "~/core/query";
import { Hint, Stat } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { Drill, DrillLink } from "~/modules/analytics/components/detail";
import { CONTENT_ICON } from "~/modules/analytics/components/readings";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { matches, TableTools } from "~/modules/analytics/components/table";
import { TreeNode } from "~/modules/analytics/components/tree";
import { PageDistributionRows } from "~/modules/analytics/features/platform-publishing/page-distribution";
import { CONTENT_PATH, readingPath } from "~/modules/analytics/lib/loop";

const FILTERS: ContentFilter[] = ["all", "pending", "drafts", "published", "dismissed"];

/**
 * The brand's content, as a tree: each gap, the articles written for it, and
 * where each article went.
 *
 * The gap is the object a person works on here — the question the brand loses
 * — and everything else hangs off it. The states and the filters are the
 * server's, from the same function that counts the overview's loop strip, so
 * "4 pending" there is these four rows here.
 */
export function ContentPage({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const { locale } = useI18n();
	const { editor, why } = useAccess();
	const agentRun = useAgentRun();
	const runWithAgent = useRunWithAgentLabel();
	const queryClient = useQueryClient();
	const refresh = () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) });

	// In the address, so the overview's loop strip, a to-do and the editor can
	// each open the tree on the queue they are about.
	const [params, setParams] = useSearchParams();
	const asked = params.get("show");
	const filter: ContentFilter = FILTERS.includes(asked as ContentFilter) ? (asked as ContentFilter) : "all";
	const pick = (value: ContentFilter) =>
		setParams(
			(previous) => {
				const next = new URLSearchParams(previous);
				if (value === "all") next.delete("show");
				else next.set("show", value);
				return next;
			},
			{ replace: true },
		);
	const [query, setQuery] = useState("");
	const [toggled, setToggled] = useState<ReadonlySet<string>>(new Set());
	const [adding, setAdding] = useState(false);
	const [gapText, setGapText] = useState("");
	const [briefGap, setBriefGap] = useState<ContentGap | null>(null);
	const [agentGap, setAgentGap] = useState<string | null>(null);
	const [taking, setTaking] = useState<ContentArticle | null>(null);
	const [busy, setBusy] = useState<string | null>(null);

	const state = useQuery({
		queryKey: [
			...keys.entity(entityId),
			"content",
			{ range: filters.range, engine: filters.engine, locale: filters.locale, topics: filters.topics },
		],
		queryFn: () => api.content(entityId, filters),
		...read,
		// Articles being written land here while it is open.
		refetchInterval: (q) => (q.state.data?.counts.drafts ? 10_000 : false),
	});
	const change = useMutation({ mutationFn: (run: () => Promise<unknown>) => run(), onSuccess: refresh });

	async function act(id: string, run: () => Promise<unknown>, done?: string): Promise<boolean> {
		setBusy(id);
		try {
			await change.mutateAsync(run);
			if (done) toast.success(done);
			return true;
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
			return false;
		} finally {
			setBusy(null);
		}
	}

	async function addGap(event: React.FormEvent) {
		event.preventDefault();
		if (await act("add", () => api.addGap(entityId, gapText, filters.locale), t("common.saved"))) {
			setGapText("");
			setAdding(false);
		}
	}

	// Articles are the point of the drafts and published views, so their gaps
	// open by default there; a click turns one the other way.
	const openByDefault = filter === "drafts" || filter === "published";
	const isOpen = (id: string) => openByDefault !== toggled.has(id);
	const toggle = (id: string) =>
		setToggled((previous) => {
			const next = new Set(previous);
			if (!next.delete(id)) next.add(id);
			return next;
		});

	return (
		<StageShell
			entityId={entityId}
			reading={CONTENT_PATH}
			title={t("nav.content")}
			icon={CONTENT_ICON}
			lede={t("content.lede")}
			filters={filters}
			setFilters={setFilters}
			actions={
				<>
					<Chip
						leftIcon={Plus}
						type="button"
						disabled={!editor}
						title={whyDisabled(editor, why.editor)}
						onClick={() => setAdding(!adding)}
					>
						{adding ? t("common.cancel") : t("manual.addGap")}
					</Chip>
					{editor ? (
						<ChipLink variant="primary" href={articlePath(entityId)}>
							{t("compose.title")}
						</ChipLink>
					) : null}
				</>
			}
		>
			{adding ? (
				<form className="panel" onSubmit={addGap} style={{ marginBlock: "var(--s-6)" }}>
					<fieldset className="bare-fieldset stack" disabled={!editor || busy === "add"}>
						<label htmlFor="manual-gap-question">
							{t("manual.gapQuestion")}
							<Hint text={t("manual.gapHelp")} />
							<ChipInput
								id="manual-gap-question"
								value={gapText}
								onChange={(event) => setGapText(event.target.value)}
								minLength={8}
								maxLength={300}
								required
							/>
						</label>
						<div className="row-actions">
							<Button type="submit">{t("common.save")}</Button>
						</div>
					</fieldset>
				</form>
			) : null}

			<Async state={state}>
				{(data) => {
					const inFilter = (gap: ContentGap) =>
						filter === "all" ? gap.state !== "dismissed" : gap.filters.includes(filter);
					const shown = data.gaps.filter(
						(gap) =>
							inFilter(gap) &&
							matches(
								query,
								gap.question ?? t("content.defaultGap"),
								gap.topic ?? "",
								...gap.signals.rivals,
								...gap.articles.map((a) => a.title),
							),
					);
					const total = data.gaps.filter(inFilter).length;

					return (
						<>
							<div className="stats insight-stats">
								<Stat
									label={t("content.pending")}
									hint={t("content.pendingHint")}
									value={String(data.counts.pending)}
								/>
								<Stat label={t("content.drafts")} hint={t("content.draftsHint")} value={String(data.counts.drafts)} />
								<Stat
									label={t("content.published")}
									hint={t("content.publishedHint")}
									value={String(data.counts.published)}
								/>
							</div>

							<TableTools
								query={query}
								onQuery={setQuery}
								placeholder={t("content.search")}
								shown={shown.length}
								total={total}
							>
								<ChipSelect
									value={filter}
									onChange={(value) => pick(value as ContentFilter)}
									aria-label={t("content.filter")}
									displayLabel={`${t("content.filter")}: ${t(`content.filter.${filter}`)}`}
									align="end"
									options={FILTERS.map((value) => ({
										value,
										label:
											value === "all"
												? t("content.filter.all")
												: `${t(`content.filter.${value}`)} · ${countOf(data.counts, value)}`,
									}))}
								/>
							</TableTools>

							{shown.length === 0 ? (
								<EmptyState size="compact" title={query ? t("table.noMatch") : t(`content.empty.${filter}`)} />
							) : (
								<div className="table-scroll">
									<Table variant="resource" className="content-tree">
										<TableHeader>
											<TableRow>
												<TableHead className="wrap">{t("content.gap")}</TableHead>
												<TableHead>{t("table.status")}</TableHead>
												<TableHead className="wrap">{t("content.losing")}</TableHead>
												<TableHead className="num">{t("pages.crawled")}</TableHead>
												<TableHead className="num">{t("pages.cited")}</TableHead>
												<TableHead />
											</TableRow>
										</TableHeader>
										{shown.map((gap) => (
											<GapRows
												key={gap.id}
												entityId={entityId}
												gap={gap}
												filter={filter}
												open={isOpen(gap.id)}
												onToggle={() => toggle(gap.id)}
												busy={busy}
												menu={
													<>
														<DropdownMenuItem onSelect={() => setBriefGap(gap)}>
															{t("content.viewBrief")}
														</DropdownMenuItem>
														{gap.default ? null : (
															<DropdownMenuItem
																disabled={!editor || busy === gap.id}
																onSelect={() =>
																	act(
																		gap.id,
																		() => api.dismissGap(gap.id, gap.state === "dismissed"),
																		t(gap.state === "dismissed" ? "content.restored" : "content.dismissed"),
																	)
																}
															>
																{t(gap.state === "dismissed" ? "content.restore" : "content.dismiss")}
															</DropdownMenuItem>
														)}
														{gap.default ? null : (
															<DropdownMenuItem
																disabled={!agentRun.allowed}
																title={agentRun.why}
																onSelect={() => setAgentGap(gap.id)}
															>
																{runWithAgent}
															</DropdownMenuItem>
														)}
													</>
												}
												onApprove={(article) =>
													act(article.id, async () => {
														const result = await api.approvePage(article.id);
														if (result.status !== "published") toast.info(t("pages.publishPending"));
													})
												}
												onTakeDown={setTaking}
											/>
										))}
									</Table>
								</div>
							)}
						</>
					);
				}}
			</Async>

			{briefGap ? (
				<ChipModal
					open
					size="xl"
					onOpenChange={(open) => (open ? null : setBriefGap(null))}
					srTitle={t("content.brief")}
				>
					<ChipModalHeader onClose={() => setBriefGap(null)} closeAriaLabel={t("common.close")}>
						{briefGap.question ?? t("content.defaultGap")}
					</ChipModalHeader>
					<ChipModalBody className="px-(--s-8)">
						<GapBrief entityId={entityId} gapId={briefGap.id} locale={filters.locale} />
					</ChipModalBody>
				</ChipModal>
			) : null}
			{agentGap ? (
				<RunWithAgentModal brandId={entityId} event="gap_found" objectId={agentGap} onClose={() => setAgentGap(null)} />
			) : null}
			<ChipConfirmModal
				open={taking !== null}
				onOpenChange={(open) => (open ? null : setTaking(null))}
				title={t("pages.unpublishTitle")}
				text={t("pages.unpublishBody")}
				dismissLabel={t("common.cancel")}
				confirm={{
					label: t("pages.unpublish"),
					variant: "destructive",
					onClick: () => {
						if (taking) void act(taking.id, () => api.unpublishPage(taking.id));
						setTaking(null);
					},
				}}
			/>
		</StageShell>
	);
}

function countOf(counts: ContentCounts, f: Exclude<ContentFilter, "all">): number {
	return counts[f];
}

/** One gap and, when open, its articles and each article's distribution. */
function GapRows({
	entityId,
	gap,
	filter,
	open,
	onToggle,
	busy,
	menu,
	onApprove,
	onTakeDown,
}: {
	entityId: string;
	gap: ContentGap;
	filter: ContentFilter;
	open: boolean;
	onToggle: () => void;
	busy: string | null;
	menu: ReactNode;
	onApprove: (article: ContentArticle) => void;
	onTakeDown: (article: ContentArticle) => void;
}) {
	const t = useT();
	const { locale } = useI18n();
	const { editor, why } = useAccess();
	const [distribution, setDistribution] = useState<ReadonlySet<string>>(new Set());
	// Under the drafts and published views, the articles those views are about.
	const articles = gap.articles.filter((a) =>
		filter === "drafts" ? a.status === "draft" || a.status === "review" : filter === "published" ? a.live : true,
	);
	const fetched = gap.articles.reduce((sum, a) => sum + a.fetched, 0);
	const cited = gap.articles.reduce((sum, a) => sum + a.cited, 0);

	return (
		<TableBody className="tree-group">
			<TableRow>
				<TableCell className="wrap">
					<TreeNode
						depth={0}
						icon={gap.default ? <FolderOpen /> : <MessageCircleQuestion />}
						open={open}
						onToggle={gap.articles.length ? onToggle : undefined}
						label={t("content.articlesOf", { n: gap.articles.length })}
					>
						<div className="tree-title">
							{gap.default ? (
								<>
									<strong>{t("content.defaultGap")}</strong>
									<Hint text={t("content.defaultGapHelp")} />
								</>
							) : gap.question_id ? (
								// The answers that make the gap checkable.
								<Drill view={{ kind: "answers", label: gap.question ?? "", filters: { question: gap.question_id } }}>
									{gap.question}
								</Drill>
							) : (
								gap.question
							)}
							<div className="faint">
								{[
									gap.default ? null : gap.topic,
									t("content.articleCount", { n: gap.articles.length }),
									gap.default
										? null
										: gap.signals.weeks > 0
											? t("content.weeks", { n: gap.signals.weeks, week: gap.signals.latest_week ?? "" })
											: t("manual.gapSource"),
								]
									.filter(Boolean)
									.join(" · ")}
							</div>
						</div>
					</TreeNode>
				</TableCell>
				<TableCell>
					{gap.default && gap.state === "open" ? null : (
						<span className={stateClass(gap.state)}>{t(`content.state.${gap.state}`)}</span>
					)}
				</TableCell>
				<TableCell className="wrap">
					<div className="tree-chips">
						{gap.signals.rivals.slice(0, 2).map((domain) => (
							<Drill className="pill" view={{ kind: "rival", label: domain, domain }} key={domain}>
								{domain}
							</Drill>
						))}
					</div>
					{gap.signals.engines.length > 0 ? (
						<div className="faint">
							{gap.signals.engines.slice(0, 3).join(", ")}
							{gap.signals.engines.length > 3 ? ` +${gap.signals.engines.length - 3}` : ""}
						</div>
					) : null}
				</TableCell>
				<TableCell className="num mono">{fetched || "—"}</TableCell>
				<TableCell className="num mono">{cited || "—"}</TableCell>
				<TableCell>
					<div className="record-actions">
						{editor ? (
							<Button asChild variant="default">
								<Link
									to={articlePath(entityId, undefined, { gap: gap.id, ...(gap.locale ? { locale: gap.locale } : {}) })}
								>
									{t("content.write")}
								</Link>
							</Button>
						) : (
							<Button variant="default" disabled title={whyDisabled(editor, why.editor)}>
								{t("content.write")}
							</Button>
						)}
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="default" size="icon" aria-label={t("table.moreActions")} disabled={busy === gap.id}>
									<MoreHorizontal />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">{menu}</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</TableCell>
			</TableRow>
			{open
				? articles.map((article) => (
						<Fragment key={article.id}>
							<TableRow>
								<TableCell className="wrap">
									<TreeNode
										depth={1}
										icon={<FileText />}
										open={distribution.has(article.id)}
										label={`${t("pages.distribution")}: ${article.title}`}
										onToggle={
											article.live
												? () =>
														setDistribution((previous) => {
															const next = new Set(previous);
															if (!next.delete(article.id)) next.add(article.id);
															return next;
														})
												: undefined
										}
									>
										<div>
											<DrillLink to={articlePath(entityId, article.id)}>{article.title}</DrillLink>
											<div className="faint">
												{article.kind} · {article.locale} ·{" "}
												{fmt.relative(locale, article.published_at ?? article.updated_at)}
											</div>
										</div>
									</TreeNode>
								</TableCell>
								<TableCell>
									<span className={statusClass(article.status)}>{t(`pages.status.${article.status}`)}</span>
									{article.conclusion ? <div className="faint">{t(`content.state.${article.conclusion}`)}</div> : null}
								</TableCell>
								<TableCell />
								<TableCell className="num mono">
									{article.fetched ? (
										<Drill view={{ kind: "crawler", label: article.title, path: new URL(article.url).pathname }}>
											{article.fetched}
										</Drill>
									) : (
										"—"
									)}
								</TableCell>
								<TableCell className="num mono">
									{article.cited ? (
										<Drill view={{ kind: "citations", label: article.title, filters: { page: article.id } }}>
											{article.cited}
										</Drill>
									) : (
										"—"
									)}
								</TableCell>
								<TableCell>
									<div className="record-actions">
										<Button asChild variant="default">
											<Link to={articlePath(entityId, article.id)}>{t("content.edit")}</Link>
										</Button>
										{article.status === "review" || article.live ? (
											<DropdownMenu>
												<DropdownMenuTrigger asChild>
													<Button variant="default" size="icon" aria-label={t("table.moreActions")}>
														<MoreHorizontal />
													</Button>
												</DropdownMenuTrigger>
												<DropdownMenuContent align="end">
													{article.status === "review" ? (
														<DropdownMenuItem
															disabled={busy === article.id || !editor}
															onSelect={() => onApprove(article)}
														>
															{t("pages.approve")}
														</DropdownMenuItem>
													) : null}
													{article.live ? (
														<>
															<DropdownMenuItem asChild>
																<a href={article.url} target="_blank" rel="noopener">
																	{t("pages.view")}
																</a>
															</DropdownMenuItem>
															<DropdownMenuItem asChild>
																<Link
																	to={`${readingPath(`/w/${entityId}`, "distribution")}?tab=prepare&page=${encodeURIComponent(article.id)}`}
																>
																	{t("editor.distribute")}
																</Link>
															</DropdownMenuItem>
															<DropdownMenuItem
																disabled={busy === article.id || !editor}
																onSelect={() => onTakeDown(article)}
															>
																{t("pages.unpublish")}
															</DropdownMenuItem>
														</>
													) : null}
												</DropdownMenuContent>
											</DropdownMenu>
										) : null}
									</div>
								</TableCell>
							</TableRow>
							{distribution.has(article.id) ? <PageDistributionRows entityId={entityId} pageId={article.id} /> : null}
						</Fragment>
					))
				: null}
		</TableBody>
	);
}

/** State is semantic colour: good, bad, waiting on someone, or nothing yet. */
function stateClass(state: GapState): string {
	switch (state) {
		case "improved":
			return "pill pill-success";
		case "not_improved":
			return "pill pill-danger";
		case "open":
		case "in_review":
			return "pill pill-warning";
		case "writing":
			return "pill pill-accent";
		case "dismissed":
			return "pill pill-quiet";
		default:
			return "pill";
	}
}

function statusClass(status: string): string {
	switch (status) {
		case "published":
			return "pill pill-success";
		case "review":
			return "pill pill-warning";
		case "unpublished":
			return "pill pill-danger";
		default:
			return "pill";
	}
}
